// src/admin/pages/AdminCustomerProfile.js
import React, { useEffect, useState, useContext } from "react";
import { useParams, useNavigate } from "react-router-dom";
import AdminSidebar from "../AdminSidebar";
import AdminHeader from "../AdminHeader";
import {
    FaArrowLeft,
    FaUser,
    FaEnvelope,
    FaPhone,
    FaShoppingCart,
    FaMoneyBill,
    FaCalendar,
    FaUserSlash,
    FaUserShield
} from "react-icons/fa";
import { getAdminCustomerById, toggleAdminCustomerStatus } 
from "../../services/adminCustomers.api";
import { AdminLanguageContext } from "../context/AdminLanguageContext";

function AdminCustomerProfile() {
    const { id } = useParams();
    const navigate = useNavigate();

    const [isOpen, setIsOpen] = useState(false);
    const [customer, setCustomer] = useState(null);
    const [loading, setLoading] = useState(true);

    const { lang } = useContext(AdminLanguageContext);

    const TEXT = {
        EN: {
            back: "Back to Customers",
            title: "Customer Profile",
            joined: "Joined",
            lastLogin: "Last Login",
            totalSpent: "Total Spent",
            orders: "Orders",
            block: "Block Customer",
            unblock: "Unblock Customer",
            address: "Address",
            orderHistory: "Order History",
            orderId: "Order ID",
            date: "Date",
            total: "Total",
            status: "Status",
            action: "Action",
            view: "View",
            loading: "Loading customer information...",
        },
        AR: {
            back: "العودة إلى العملاء",
            title: "ملف العميل",
            joined: "تاريخ التسجيل",
            lastLogin: "آخر تسجيل دخول",
            totalSpent: "إجمالي الإنفاق",
            orders: "عدد الطلبات",
            block: "حظر العميل",
            unblock: "إلغاء الحظر",
            address: "العنوان",
            orderHistory: "سجل الطلبات",
            orderId: "رقم الطلب",
            date: "التاريخ",
            total: "الإجمالي",
            status: "الحالة",
            action: "الإجراء",
            view: "عرض",
            loading: "جاري تحميل بيانات العميل...",
        },
    }[lang];

    useEffect(() => {
  setLoading(true);

  getAdminCustomerById(id)
    .then(setCustomer)
    .catch(console.error)
    .finally(() => setLoading(false));
}, [id]);


    const toggleStatus = async () => {
  await toggleAdminCustomerStatus(customer.id);

  setCustomer((c) => ({
    ...c,
    status: c.status === "active" ? "blocked" : "active",
  }));
};


    if (loading || !customer) {
        return (
            <div className={`flex bg-gray-100 min-h-screen ${isOpen ? "overflow-hidden" : ""}`}>
                <AdminSidebar isOpen={isOpen} setIsOpen={setIsOpen} />
                <div className="flex-1 lg:ml-64 p-10 text-center text-xl text-gray-500">
                    {TEXT.loading}
                </div>
            </div>
        );
    }

    return (
        <div className={`flex bg-gray-100 min-h-screen ${isOpen ? "overflow-hidden" : ""}`}>
            <AdminSidebar isOpen={isOpen} setIsOpen={setIsOpen} />

            <div className="flex-1 lg:ml-64 transition-all">
                <AdminHeader setIsOpen={setIsOpen} />

                <main className="p-4 sm:p-6 animate-fadeSlide">

                    {/* Back button */}
                    <button
                        className="flex items-center gap-2 mb-6 text-gray-600 hover:text-cyan-700"
                        onClick={() => navigate("/admin/customers")}
                    >
                        <FaArrowLeft /> {TEXT.back}
                    </button>

                    {/* CUSTOMER CARD */}
                    <div className="bg-white p-4 sm:p-6 rounded-xl shadow-lg mb-8">
                        <h1 className="text-2xl font-bold text-cyan-700 mb-4">
                            {TEXT.title}
                        </h1>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">

                            {/* Left */}
                            <div className="space-y-3">
                                <p className="flex items-center gap-3 text-lg">
                                    <FaUser className="text-cyan-600" />
                                    <span className="font-semibold">{customer.name}</span>
                                </p>

                                <p className="flex items-center gap-3 text-lg">
                                    <FaEnvelope className="text-cyan-600" />
                                    {customer.email}
                                </p>

                                <p className="flex items-center gap-3 text-lg">
                                    <FaPhone className="text-cyan-600" />
                                    {customer.phone}
                                </p>

                                <p className="flex items-center gap-3 text-lg">
                                    <FaCalendar className="text-cyan-600" />
                                    {TEXT.joined}: {customer.createdAt}
                                </p>

                                <p className="flex items-center gap-3 text-lg">
                                    <FaCalendar className="text-cyan-600" />
                                    {TEXT.lastLogin}: {customer.lastLogin}
                                </p>

                                <p className="flex items-center gap-3 text-lg">
                                    <FaMoneyBill className="text-green-600" />
                                    {TEXT.totalSpent}:
                                    <span className="font-bold">${customer.totalSpent}</span>
                                </p>

                                <p className="flex items-center gap-3 text-lg">
                                    <FaShoppingCart className="text-yellow-600" />
                                    {TEXT.orders}:
                                    <span className="font-bold">{customer.ordersCount}</span>
                                </p>

                                <span
                                    className={`px-4 py-1 rounded-full font-semibold text-sm ${
                                        customer.status === "active"
                                            ? "bg-green-100 text-green-700"
                                            : "bg-red-100 text-red-700"
                                    }`}
                                >
                                    {customer.status}
                                </span>

                                <button
                                    onClick={toggleStatus}
                                    className="mt-4 px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 mx-2"
                                >
                                    {customer.status === "active" ? (
                                        <span className="flex items-center gap-2 m">
                                            <FaUserSlash /> {TEXT.block}
                                        </span>
                                    ) : (
                                        <span className="flex items-center gap-2">
                                            <FaUserShield /> {TEXT.unblock}
                                        </span>
                                    )}
                                </button>
                            </div>

                            {/* Address */}
                            <div className="bg-gray-50 p-4 rounded-xl border">
                                <h3 className="text-lg font-bold text-gray-700 mb-2">
                                    {TEXT.address}
                                </h3>
                                <p className="text-gray-600">{customer.address}</p>
                            </div>
                        </div>
                    </div>

                    {/* ORDER HISTORY */}
                    <div className="bg-white p-4 sm:p-6 rounded-xl shadow-lg">
                        <h2 className="text-xl font-bold text-cyan-700 mb-4">
                            {TEXT.orderHistory}
                        </h2>

                        <div className="hidden md:block overflow-x-auto">
                            <table className="w-full min-w-[700px]">
                                <thead className="bg-gray-200 text-gray-700">
                                    <tr>
                                        <th className="p-3 text-center">{TEXT.orderId}</th>
                                        <th className="p-3 text-center">{TEXT.date}</th>
                                        <th className="p-3 text-center">{TEXT.total}</th>
                                        <th className="p-3 text-center">{TEXT.status}</th>                                    </tr>
                                </thead>

                                <tbody>
                                    {customer.orders.map((o) => (
                                        <tr key={o.id} className="border-b hover:bg-gray-50">
                                            <td className="p-3 font-semibold text-center">{o.id}</td>
                                            <td className="p-3 text-center">{o.date}</td>
                                            <td className="p-3 font-bold text-center">${o.total}</td>
                                            <td className="p-3 text-center">
                                                <span
  className={`px-3 py-1 rounded-full text-sm font-semibold ${
    o.status === "pending"
      ? "bg-orange-100 text-orange-700"
      : o.status === "paid"
      ? "bg-green-100 text-green-700"
      : o.status === "shipped"
      ? "bg-blue-100 text-blue-700"
      : o.status === "delivered"
      ? "bg-emerald-100 text-emerald-700"
      : o.status === "cancelled"
      ? "bg-red-100 text-red-700"
      : o.status === "refunded"
      ? "bg-gray-200 text-gray-700"
      : "bg-gray-100 text-gray-600"
  }`}
>
  {o.status}
</span>

                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>

                </main>
            </div>
        </div>
    );
}

export default AdminCustomerProfile;
